import React, { useEffect, useState, Fragment } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { createRoot } from '@wordpress/element';
import { Tab, TabsList, TabPanel, Tabs } from '@mui/base'; // https://mui.com/base-ui/react-tabs/
import { displayErrorMessage, displayLoader, ConnectionContext, getConnectionInfo, getEndpoint, getFallbackImage } from './utils';
import { getStoredData } from "./utils/data-pulls";
import { AcaSelect } from "./components/forms/acaSelect";
import { Option } from '@mui/base/Option';
import { Sidebar } from "./components";
import { TeamLogo } from './components/teamLogo';


const DirectoryApp = (props) => {
    const { 
        ...restProps 
    } = props;

    // Error and load handling
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(true);

    // Connection Information
    const [connectionInfo, setConnectionInfo] = useState(null);

    // Layout Pieces
    const [ view, setView ] = useState(null);

    // Search Data
    const [ searchParams, setSearchParams ] = useState(null);
    const [ competitions, setCompetitions ] = useState(null);
    const [ results, setResults ] = useState(null);
    const [ initialMessage, setInitialMessage ] = useState(true);

    /**
     * On init:
     * 1 - Get our connection info
     * 2 - Get list of competitions
     * 3 - Check for directory view in URL
     */
    useEffect(() => {

        // Get our connection info
        getConnectionInfo().then(
            (result) => {
                setConnectionInfo(result.data);
            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        let args = [
            {
                key: 'competitions',
                type: 'has_seasons'
            }
        ];

        // Get our list of competitions
        getStoredData(args).then(
            (result) => {

                // Set Competitions
                if ( result.data.competitions ) {
                    setCompetitions(result.data.competitions);
                }

                // Set loaded
                setIsLoaded(true);

            }
        ).catch(
            (error) => {
                setError(error);
            }
        );

        // Get & set initial directory view
        const queryParameters = new URLSearchParams(window.location.search);
        const view = queryParameters.get("view");
        const validViews = ['players', 'clubs'];
        if ( validViews.includes(view) ) {
            setView(view);
        }

    }, []);

    /**
     * Query database
     */
    useEffect(() => {
        const timeOutId = setTimeout(() => {
            if ( !view ) return;
            if ( !searchParams || (Object.keys(searchParams).length === 0 && searchParams.constructor === Object) ) {
                setInitialMessage(true);
                setResults(null);
                return;
            }
    
            setIsLoaded(false);

            // Put together data query
            let args = [];
            switch (view) {
                case 'clubs':
                    args.push({ 
                        key: 'clubs',
                        type: 'directory',
                        name: searchParams.hasOwnProperty('name') ? searchParams.name : null,
                        competition: searchParams.hasOwnProperty('competition') ? searchParams.competition : null
                    });
                    break;
                case 'players':
                    args.push({ 
                        key: 'players',
                        searchBy: 'directory',
                        name: searchParams.hasOwnProperty('name') ? searchParams.name : null,
                        club: searchParams.hasOwnProperty('club') ? searchParams.club : null
                    });
                    break;
            }

            // Get stored data
            getStoredData(args).then(
                (result) => {

                    // Set clubs data
                    if ( view == 'clubs' ) {
                        setInitialMessage(false);
                        if ( result.data.clubs ) {
                            setResults(result.data.clubs);
                        } else {
                            setResults(null);
                        }
                    }

                    // Set players data
                    if ( view == 'players' ) {
                        setInitialMessage(false);
                        if ( result.data.players ) {
                            setResults(result.data.players);
                        } else {
                            setResults(null);
                        }
                    }
                    setIsLoaded(true);
    
                    
                }
            ).catch(
                (error) => {
                    setError(error);
                }
            );


        }, 500);
        return () => clearTimeout(timeOutId);
    }, [searchParams]);


    /**
     * Handle search form inputs
     */
    const handleSearchInput = (field, value) => {
        
        // Only search if more than 3 characters have been entered
        if ( value.length == 0 ) {
            updateSearchParam(field, null);
        } else if ( value.length >= 3 ) {
            updateSearchParam(field, value);
        }
        

    }

    /** 
     * Handle competition dropdown
     */
    const handleCompetitionChange = (event, value) => {
        updateSearchParam('competition', value);
    }


    /**
     * Update/change search parameter
     */
    const updateSearchParam = (field, value) => {
        let params;
        if ( searchParams ) {
            params = {...searchParams};
        } else {
            params = {};
        }
        if ( value ) {
            params[field] = value;
        } else {
            delete params[field];
        }
        setSearchParams(params);
    }


    /**
     * Change directory view
     */
    const handleChangeView = (view) => {
        setView(view);
        setResults(null);
        setInitialMessage(true);
        const queryParameters = new URLSearchParams(window.location.search);
        queryParameters.set("view", view);
        setSearchParams(null);
        history.pushState(null, null, "?"+queryParameters.toString());
    }


    return (
        <ConnectionContext.Provider value={connectionInfo}>
            <section className="acaPage acaPage--directory">
                
                <div className="acaHeader acaHeader--center acaHeader--directory">
                    <div className="acaHeader__inner">
                        <h1 className="acaH1--alt">Directory</h1>
                    </div>
                </div>

                { !view ? (
                    <div className="acaWrapper acaMain directoryView directoryView--default">
                        <h2 className="acaH2">What are you searching for?</h2>
                        <div className="directoryBtns">
                            <button onClick={() => handleChangeView('clubs')} className="acaBtn acaBtn--large acaBtn--solid acaBtn--red acaBtn--soccerRegularIcon">
                                Club
                            </button>
                            <button onClick={() => handleChangeView('players')} className="acaBtn acaBtn--large acaBtn--solid acaBtn--blue acaBtn--runnerRegularIcon">
                                Player
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="directoryView directoryView--search">
                        <Tabs className="acaPage acaPage--directory" slots={{ root: 'section' }} value={ view }  onChange={ (event, newValue) => handleChangeView(newValue) }>

                            <div className="acaLargeTabs__border">
                                <TabsList className="acaHeader__tabs acaLargeTabs acaLargeTabs--light acaWrapper acaWrapper--withPadding" >
                                        <Tab value="clubs"><span className="acaIcon acaIcon--red acaIcon--soccer"></span> Club Directory</Tab>
                                        <Tab value="players"><span className="acaIcon acaIcon--blue acaIcon--runner"></span> Player Directory</Tab>
                                </TabsList>
                            </div>

                            { /** Club Directory */ }
                            <TabPanel value="clubs">
                                <div className="acaPage acaPage--withSidebar">
                                    <Sidebar>
                                        <div className="formBox">

                                            <div className="acaField">
                                                <span className="acaField__label">
                                                    Search By Name
                                                </span>
                                                <div className="acaField__inputWrapper">
                                                    <input className="acaField__input acaInput" placeholder="Enter at least 3 letters" onChange={(e) => handleSearchInput('name', e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="acaField">
                                                <span className="acaField__label">
                                                    Search By Competition
                                                </span>
                                                <div className="acaField__inputWrapper">
                                                { competitions ? (
                                                    <AcaSelect onChange={handleCompetitionChange} extraClasses="acaSelect--fullWidth acaSelect--light" defaultValue="" id="schedule-competition" name="schedule-competition">
                                                            <Option value="">All Competitions</Option>
                                                        { competitions.map((competition, i) => (
                                                            <Option key={i} value={competition.id}>{competition.name}</Option>
                                                        ))}
                                                    </AcaSelect>
                                                ) : '' }
                                                </div>
                                            </div>

                                        </div>
                                    </Sidebar>
                                    <div className="acaMain acaWrapper">
                                        { initialMessage ? 
                                            (
                                                <div className="directoryMessage">
                                                    <span className="acaIcon acaIcon--extraLarge acaIcon--darkGrey acaIcon--searchThin"></span>
                                                    <p className="acaH2 aca--darkGreyText">Search by name or competition</p>
                                                </div>
                                            ) : !isLoaded ? displayLoader('acaLoader--fill') 
                                            : results && results.length ? (
                                                (
                                                    <div className="directoryList">
                                                        { results.map((result, i) => (
                                                            <DirectoryItem key={i} view="club" data={result} />
                                                        )) }
                                                    </div>
                                                )
                                            ) : 
                                            <div className="directoryMessage">
                                                <span className="acaIcon acaIcon--extraLarge acaIcon--darkGrey acaIcon--folderXThin"></span>
                                                <p className="acaH2 aca--darkGreyText">No results found, try a different search</p>
                                            </div> 
                                        }
                                        
                                    </div>
                                </div>
                            </TabPanel>

                            { /** Player Directory */ }
                            <TabPanel value="players">
                                <div className="acaPage acaPage--withSidebar">
                                    <Sidebar>
                                        <div className="formBox">

                                            <div className="acaField">
                                                <span className="acaField__label">
                                                    Search By Name
                                                </span>
                                                <div className="acaField__inputWrapper">
                                                    <input className="acaField__input acaInput" placeholder="Enter at least 3 letters" onChange={(e) => handleSearchInput('name', e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="acaField">
                                                <span className="acaField__label">
                                                    Search By Club
                                                </span>
                                                <div className="acaField__inputWrapper">
                                                    <input className="acaField__input acaInput" placeholder="Enter at least 3 letters" onChange={(e) => handleSearchInput('club', e.target.value)} />
                                                </div>
                                            </div>

                                        </div>
                                    </Sidebar>
                                    <div className="acaMain acaWrapper">
                                        {   !isLoaded ? 
                                                displayLoader('acaLoader--fill') : 
                                            initialMessage ? 
                                            (
                                                <div className="directoryMessage">
                                                    <span className="acaIcon acaIcon--extraLarge acaIcon--darkGrey acaIcon--searchThin"></span>
                                                    <p className="acaH2 aca--darkGreyText">Search by name or club</p>
                                                </div>
                                            ) : results && results.length ? (
                                                (
                                                    <div className="directoryList">
                                                        { results.map((result, i) => (
                                                            <DirectoryItem key={i} view="player" data={result} />
                                                        )) }
                                                    </div>
                                                )
                                            ) : 
                                            <div className="directoryMessage">
                                                <span className="acaIcon acaIcon--extraLarge acaIcon--darkGrey acaIcon--folderXThin"></span>
                                                <p className="acaH2 aca--darkGreyText">No results found, try a different search</p>
                                            </div> 
                                        }
                                        
                                    </div>
                                </div>
                            </TabPanel>
                        </Tabs>

                    </div>
                ) }

            </section>
        </ConnectionContext.Provider>
    );
}



const DirectoryItem = (props) => {
    const { 
        view = null,
        data = null,
        ...restProps 
    } = props;
    if ( !view || !data ) return;

    function getImage() {
        if ( view == 'club' && data.hasOwnProperty('uid') ) {
            return (<TeamLogo teamId={data.uid} />);
        } else if ( view == 'player' && data.hasOwnProperty('data') && data.data.hasOwnProperty('photo') && data.data.photo ) {
            return (
                <div className="teamLogo">
                    <img src={data.data.photo} className="teamLogo__inner" />
                </div>
            );
        }
    }

    function getName() {
        switch (view) {
            case 'club':
            case 'player':
                return data.hasOwnProperty('name') ? data.name : '';
                break;
        }
    }

    function getExtra() {
        switch (view) {
            case 'club':
                return '';
                break;
            case 'player':
                const teamName = data.hasOwnProperty('clubName') ? data.clubName : '';
                const teamID = data.hasOwnProperty('clubID') ? data.clubID : '';
                return (
                    <div className="directoryItem__team">
                        <TeamLogo teamId={teamID} fallback={getFallbackImage()} />
                        <p className="directoryItem__teamName">{ teamName }</p>
                    </div>
                );
                break;
        }
    }

    return (
        <a href={data.hasOwnProperty('url') ? data.url : ''} className={"directoryItem directoryItem--" + view}>
            <div className="directoryItem__img">{ getImage() }</div>
            <div className="directoryItem__content">
                <h4 className="acaH4 directoryItem__name">{ getName() }</h4>
                { getExtra() }
            </div>
            <div className="directoryItem__icon"></div>
        </a>
    );
}

export default DirectoryApp;

// Render Match Page
const appContainer = document.getElementById('directoryApp');
const root = createRoot(appContainer);
root.render(<DirectoryApp />);